import type { CSSProperties } from 'react'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { canvasToScreen } from '../interaction/canvasToScreen'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'
import { Bubble } from './Bubble'

const BUBBLE_WIDTH = 50
const BUBBLE_HEIGHT = 56
// Parity with iOS 238.2: plain 200ms opacity fade, NO scale, NO spring.
// iOS landed on `.easeInOut(duration: 0.2)` + `.transition(.opacity)`;
// Android uses animateFloatAsState(tween(200, FastOutSlowInEasing)).
const CROSSFADE_MS = 200

export interface BubbleOverlayHandle {
  /** Called each rAF tick with current transform; updates bubble screen position imperatively. */
  apply(zoom: number, panX: number, panY: number, dpr: number): void
}

interface Props {
  selected: SelectedStationSnapshot | null
  fillColorForLine: (lineId: string) => string
  /**
   * Label rendered inside the bubble. Defaults to `'A'` for backwards
   * compatibility, but the route-builder flow passes `'B'` whenever the
   * "from" slot is already filled — so the user sees a hint that the
   * NEXT pick will land in the "to" slot, not "from".
   *
   * @defaultValue `'A'`
   */
  text?: string
  /**
   * Getter for the current map transform. Called in [useLayoutEffect]
   * whenever the bubbles list changes so a newly-mounted bubble is
   * positioned BEFORE first paint — otherwise the element ships
   * untranslated for one frame (until the host's next rAF tick fires
   * `apply()`), visibly flashing at the viewport origin.
   * Must be a stable reference (wrap in `useCallback` or pass the
   * ref object itself) — it is called during layout.
   */
  getTransform: () => { zoom: number; panX: number; panY: number; dpr: number }
}

/** Fade-lifecycle state for a single bubble instance. */
interface InFlightBubble {
  snapshot: SelectedStationSnapshot
  state: 'entering' | 'visible' | 'leaving'
}

/**
 * Renders up to 2 Bubble instances concurrently during A→B crossfade,
 * plus handles null ↔ snapshot fade-in / fade-out. Position updates
 * imperatively via [apply] to avoid React reconciliation on every rAF
 * tick while the user is panning. Fade managed by CSS transitions; a
 * setTimeout unmounts each 'leaving' bubble after CROSSFADE_MS.
 *
 * Parity with iOS 238.2 (plain 200ms opacity fade, no scale / spring).
 */
export const BubbleOverlay = forwardRef<BubbleOverlayHandle, Props>(function BubbleOverlay(
  { selected, fillColorForLine, text = 'A', getTransform },
  ref,
) {
  const [bubbles, setBubbles] = useState<InFlightBubble[]>([])
  const positionRefs = useRef<Map<string, HTMLDivElement | null>>(new Map())

  // Reconcile: mark previous leaving, insert new as 'entering'.
  // The `react-hooks/set-state-in-effect` rule discourages setState in
  // effects because of cascading renders. This effect is an exception:
  // the `selected` prop IS the external input, and each transition
  // needs to remember the previous `bubbles` state to compute the new
  // fade-lifecycle (enter new / mark old leaving / revive re-selected).
  // A render-time derivation isn't possible because we need state that
  // persists across prop changes (leaving bubbles outlive the prop
  // change by CROSSFADE_MS). useReducer would be a lateral refactor
  // with the same effect-dispatched pattern.
  useEffect(() => {
    setBubbles((prev) => {
      const next: InFlightBubble[] = []
      const selectedId = selected?.id ?? null
      let addedSelected = false
      for (const b of prev) {
        if (b.snapshot.id === selectedId) {
          // Keep visible (or re-anchor from 'leaving' back to 'visible'
          // if the user re-selected the same station mid-fade-out).
          next.push({ snapshot: b.snapshot, state: 'visible' })
          addedSelected = true
        } else {
          next.push({ snapshot: b.snapshot, state: 'leaving' })
        }
      }
      if (selected !== null && !addedSelected) {
        next.push({ snapshot: selected, state: 'entering' })
      }
      return next
    })
  }, [selected])

  // 'entering' → 'visible' after one rAF so the initial opacity:0 commits
  // to the DOM before the transition to opacity:1 kicks in. Without this
  // hop the element mounts with opacity:1 directly and no fade-in occurs.
  // jsdom does not implement rAF — fall back to setTimeout(0) so the state
  // hop still fires in test environments under vi.useFakeTimers().
  useEffect(() => {
    const hasEntering = bubbles.some((b) => b.state === 'entering')
    if (!hasEntering) return
    const promote = () => {
      setBubbles((prev) =>
        prev.map((b) => (b.state === 'entering' ? { ...b, state: 'visible' } : b)),
      )
    }
    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      const rafId = window.requestAnimationFrame(promote)
      return () => {
        window.cancelAnimationFrame(rafId)
      }
    }
    const timerId = window.setTimeout(promote, 0)
    return () => {
      window.clearTimeout(timerId)
    }
  }, [bubbles])

  // Unmount 'leaving' bubbles after CROSSFADE_MS so the opacity transition
  // can visibly play out before React removes the DOM node.
  useEffect(() => {
    const hasLeaving = bubbles.some((b) => b.state === 'leaving')
    if (!hasLeaving) return
    const timer = window.setTimeout(() => {
      setBubbles((prev) => prev.filter((b) => b.state !== 'leaving'))
    }, CROSSFADE_MS)
    return () => {
      window.clearTimeout(timer)
    }
  }, [bubbles])

  const applyTransform = useCallback(
    (zoom: number, panX: number, panY: number, dpr: number) => {
      for (const b of bubbles) {
        const el = positionRefs.current.get(b.snapshot.id)
        if (el === null || el === undefined) continue
        const { x, y } = canvasToScreen(b.snapshot.canvasX, b.snapshot.canvasY, panX, panY, zoom)
        const cssX = x / dpr
        const cssY = y / dpr
        // translate3d instead of left/top: a transform is compositor-only,
        // so per-frame repositioning never invalidates layout (Task 1.10).
        // The host is anchored at left/top 0, so the translate equals the
        // old left/top values 1:1.
        el.style.transform = `translate3d(${cssX - BUBBLE_WIDTH / 2}px, ${cssY - BUBBLE_HEIGHT}px, 0)`
      }
    },
    [bubbles],
  )

  useImperativeHandle(ref, () => ({ apply: applyTransform }), [applyTransform])

  // Position newly-mounted bubbles BEFORE first paint so they don't flash
  // at (0, 0) for one frame. Runs on every bubbles change. The host's
  // rAF-driven apply() continues to update positions on every gesture
  // tick, but that first tick doesn't come until the next render loop.
  useLayoutEffect(() => {
    const t = getTransform()
    applyTransform(t.zoom, t.panX, t.panY, t.dpr)
  }, [bubbles, applyTransform, getTransform])

  return (
    <>
      {bubbles.map((b) => {
        const isEntering = b.state === 'entering'
        const isLeaving = b.state === 'leaving'
        const opacity = isEntering || isLeaving ? 0 : 1
        const style: CSSProperties = {
          position: 'absolute',
          // Anchor at the container origin — per-frame positioning is a
          // translate3d in applyTransform, so the transform value maps
          // 1:1 to the pre-Task-1.10 left/top values.
          left: 0,
          top: 0,
          // zIndex chain (bottom → top):
          //   canvas (map geometry)                 — z=auto
          //   StationLabelsOverlay text             — z=1
          //   DOM dim layer in MetroMapCanvas       — z=2
          //   bubble host (this)                    — z=3
          // Bubble sits above the dim so it stays vivid while
          // everything below fades to backgroundPrimary. Parity with
          // iOS/Android where the engine's dim pass runs between the
          // last map pass and the (native) bubble overlay.
          zIndex: 3,
          opacity,
          transition: `opacity ${CROSSFADE_MS}ms ease-in-out`,
          willChange: 'opacity',
        }
        return (
          <div
            key={b.snapshot.id}
            data-bubble-host=""
            ref={(el) => {
              positionRefs.current.set(b.snapshot.id, el)
            }}
            style={style}
          >
            <Bubble fillColor={fillColorForLine(b.snapshot.lineId)} text={text} />
          </div>
        )
      })}
    </>
  )
})
